import { useSetState } from 'minimal-shared/hooks';
import { useMemo, useEffect, useCallback } from 'react';

import axios, { endpoints } from 'src/lib/axios';

import { AdminRoleEnum, WorkspaceEnum, ROLE_DEFAULT_WORKSPACE } from 'src/types/user';

import { JWT_STORAGE_KEY } from './constant';
import { AuthContext } from '../auth-context';
import { setSession, isValidToken } from './utils';

import type { AuthState } from '../../types';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({ user: null, loading: true });

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(JWT_STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const res = await axios.get(endpoints.auth.me);

        const { data } = res.data;

        setState({ user: { ...data, accessToken }, loading: false });
      } else {
        setState({ user: null, loading: false });
      }
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  // Workspace and permission functions
  const currentWorkspace = useMemo(() => {
    if (!state.user?.role) return WorkspaceEnum.Administrator;
    return ROLE_DEFAULT_WORKSPACE[state.user.role] || WorkspaceEnum.Administrator;
  }, [state.user?.role]);

  const canChangeWorkspace = useMemo(
    () => state.user?.role === AdminRoleEnum.Administrator,
    [state.user?.role]
  );

  const changeWorkspace = useCallback((workspace: WorkspaceEnum) => {
    // Implementation for changing workspace
    console.log('Changing workspace to:', workspace);
  }, []);

  const isAdmin = useCallback(
    (): boolean => state.user?.role === AdminRoleEnum.Administrator,
    [state.user?.role]
  );

  const isEditor = useCallback(
    (): boolean => state.user?.role === AdminRoleEnum.Editor,
    [state.user?.role]
  );

  const isEditorManager = useCallback(
    (): boolean => state.user?.role === AdminRoleEnum.EditorManager,
    [state.user?.role]
  );

  const isSupervisor = useCallback(
    (): boolean => state.user?.role === AdminRoleEnum.Supervisor,
    [state.user?.role]
  );

  const memoizedValue = useMemo(
    () => ({
      user: state.user ? { ...state.user, role: state.user?.role as AdminRoleEnum } : null,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',

      // Workspace management
      currentWorkspace,
      canChangeWorkspace,
      changeWorkspace,

      isAdmin,
      isSupervisor,
      isEditor,
      isEditorManager,
    }),
    [
      checkUserSession,
      state.user,
      status,
      currentWorkspace,
      canChangeWorkspace,
      changeWorkspace,

      isAdmin,
      isSupervisor,
      isEditor,
      isEditorManager,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
